<template>
    <!-- ACTION PLAN CHECKLIST PAGE -->
    <div v-if="checklist && checkReadPermissions()" ref="container" id="detail-content" class="action-plan content scrollbar right" :class="[$mq, { 'deadline-ended': isDeadlineEnded }]">
        <!-- HEADER -->
        <div class="top-container">
            <AppccTopDetail class="action-plan-top-detail" :title="checklist && checklist.audit_item_name ? checklist.audit_item_name : nonConformityTitle || ''" :checklist="checklist" :hasMoreOptions="false" :hasReopen="false" :hasDelete="false" :showChecklistScore="true" :checklistProgressBar="true" @backButton="goBack" />
        </div>

        <div class="selector-container">
            <div v-if="actionPlanStatuses && actionPlanStatuses.length" class="state-container" ref="stateSelector" :class="{ isOpen: isSelectorOpen }" @click="handleSelectorClick">
                <div class="current-state" v-if="currentStatus" :class="{ isOpen: isSelectorOpen, disabled: !checkPermissions(), placeholder: !checkPermissions() }">
                    <div class="ball" v-if="currentStatus.color" :style="{ backgroundColor: currentStatus.color }"></div>
                    <div class="name">
                        {{ currentStatus.name }}
                    </div>
                </div>
                <div class="current-state placeholder" v-else :class="{ isOpen: isSelectorOpen, disabled: isDeadlineEnded || !canChangeStatus || !checkPermissions() }">
                    <div class="name">
                        {{ $t('action_plans.change_status_to') }}
                    </div>
                </div>
                <div class="state-selector" v-if="isSelectorOpen" :class="{ active: isSelectorOpen }">
                    <div class="state" v-for="(state, index) in actionPlanStatuses" @click.stop="changeStatus(state.id)" :key="index" :class="{ selected: checklist && checklist.status === state.id }">
                        <div class="ball" :style="{ backgroundColor: state.color }"></div>
                        <div class="name">{{ state.name }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="action-plan-container">
            <div class="non-conformity-container">
                <i class="i-andy-exclamation i-andy-m"></i>
                <span class="non-conformity-title">{{ $t('action_plans.non_conformity') }}</span>
            </div>

            <div v-if="referenceSectionName" class="audit-name-display">{{ referenceSectionName }}</div>

            <div class="section-content audit detail landscape reference-audit-section" id="content">
                <answer v-if="referenceItem" :item="referenceItem" :list="checklist" :cId="checklist.id" :aId="referenceItem.item_id" :cType="TYPE.AUDIT" :showErrors="false" :isActionPlanPage="true" />
                <div class="section subitems" v-if="referenceItem && referenceSubitems && referenceSubitems.length > 0">
                    <span class="section-title" :class="{ closed: closedSubitemsSections.includes(referenceItem.item_id) }" @click="toggleSubitemsSection(referenceItem.item_id)">
                        <span class="subitems-icon">
                            <span v-if="closedSubitemsSections.includes(referenceItem.item_id)" class="i-andy-add i-andy-m"></span>
                            <span v-else class="i-andy-less i-andy-m"></span>
                        </span>
                        <span class="subitems-text">
                            {{
                                closedSubitemsSections.includes(referenceItem.item_id)
                                    ? $t('audit.show_subitems', {
                                          total: referenceDirectSubitemsCount
                                      })
                                    : $t('audit.close_subitems', {
                                          total: referenceDirectSubitemsCount
                                      })
                            }}
                        </span>
                    </span>
                    <div class="section-content" v-show="!closedSubitemsSections.includes(referenceItem.item_id)">
                        <answer v-for="subitem in referenceSubitems" :key="subitem.item_id" :item="subitem" :list="referenceAudit" :cId="referenceAuditId" :aId="subitem.item_id" :cType="TYPE.AUDIT" :showErrors="false" :isActionPlanPage="true" />
                    </div>
                </div>
            </div>

            <div class="action-plan-title-container">
                <i class="i-andy-exclamation i-andy-m"></i>
                <span class="action-plan-title">{{ $t('action_plans.action_plan') }}</span>
            </div>

            <div class="deadline-banner" :class="{ ended: isDeadlineEnded }" v-if="checklist && checklist.end_date">
                <i :class="isDeadlineEnded ? 'i-andy-exclamation i-andy-m' : 'i-andy-calendar i-andy-m'"></i>
                <span
                    class="deadline-message"
                    v-html="
                        $t(isDeadlineEnded ? 'action_plans.deadline_message_ended' : 'action_plans.deadline_message', {
                            date: formatDeadlineDate(checklist.end_date)
                        })
                    "
                >
                </span>
            </div>

            <div class="action-plan-items-container">
                <!-- Action Plan Items Section -->
                <answer v-for="answer in visibleItems" :key="answer.item_id" :item="answer" :list="checklist" :cId="checklist.id" :aId="answer.item_id" :cType="19" :class="{ 'deadline-disabled': isDeadlineEnded }" :isActionPlanCompleted="isActionPlanCompleted" :isDeadlineEnded="isDeadlineEnded" :permissions="detailPermissions()" />
            </div>
        </div>
    </div>

    <div v-else-if="checklist && !checkReadPermissions()" class="action-plan content hide-scrollbar right empty">
        <EmptyPage :buttonText="$t('empty_pages.return_audits')" @buttonAction="goToAuditList" buttonClass="back" :type="'error'" :title="$t('assets.overlay.timeout.message')" :description="$t('empty_pages.error_action_plans')" />
    </div>

    <div v-else class="action-plan content hide-scrollbar right empty">
        <EmptyPage @buttonAction="goBack" buttonClass="back" :type="'error'" :title="$t('assets.overlay.timeout.message')" :description="$t('action_plans.error_loading')" />
    </div>
</template>

<script>
import EmptyPage from '../components/layout/EmptyPage.vue'
import AppccTopDetail from '@/components/layout/AppccTopDetail'
import SelectorPopup from '../components/inputs/SelectorPopup.vue'
import answer from '@/components/domain/tasks/answer'
import { getVisibleDescendants } from '@/store/utils/checklistItems'
import { TOOLS, TYPE, FORMAT } from '@/constants'

export default {
    name: 'ActionPlan',
    components: { EmptyPage, AppccTopDetail, SelectorPopup, answer },
    data() {
        return {
            TYPE,
            isSelectorOpen: false,
            closedSubitemsSections: [],
            previousRoute: null,
            hasShownCompletionPopup: false
        }
    },
    computed: {
        actionPlanStatuses() {
            const statuses = this.$store.getters['actionplan/getAccountActionPlansStatuses']
            if (!statuses || statuses.length === 0) {
                return []
            }

            return [...statuses].sort((a, b) => {
                const typeA = parseInt(a.type) || 0
                const typeB = parseInt(b.type) || 0

                if (typeA !== typeB) {
                    return typeA - typeB
                }

                const defaultA = a.default === true || a.default === 1 || a.default === '1'
                const defaultB = b.default === true || b.default === 1 || b.default === '1'

                if (defaultA && !defaultB) {
                    return -1
                }
                if (!defaultA && defaultB) {
                    return 1
                }

                return 0
            })
        },
        currentStatus() {
            if (this.checklist && this.checklist.status && this.actionPlanStatuses.length > 0) {
                return this.actionPlanStatuses.find((status) => status.id == this.checklist.status)
            }
            return null
        },
        checklist() {
            // Get checklist from Vuex store for automatic reactivity
            const checklistId = this.$route.params.id
            let checklist = this.$store.getters['actionplan/getById'](checklistId)
            if (checklist && typeof checklist === 'object') {
                checklist.title = checklist.actionplan_name || checklist.title || ''
            }
            return checklist
        },
        nRoles() {
            // Derived from checklist - automatically resets when checklist changes
            if (this.checklist && this.checklist.config && this.checklist.config.nRoles) {
                return this.checklist.config.nRoles
            }
            return null
        },
        nonConformityTitle() {
            // Get the title from the linked audit item
            if (this.linkedAuditItem && this.linkedAuditItem.title) {
                return this.linkedAuditItem.title
            }
            // Fallback to checklist title
            if (this.checklist && this.checklist.title) {
                return this.checklist.title
            }
            return ''
        },
        linkedAuditItem() {
            // Get the audit item that is linked to this action plan
            // The reference is stored in checklist.data.ref.item
            if (this.checklist && this.checklist.items && this.checklist.data && this.checklist.data.ref) {
                const refItemId = this.checklist.data.ref.item
                // Find the item in the checklist items
                const item = Object.values(this.checklist.items).find((i) => i.data && i.data.ref_item === refItemId)
                return item || null
            }
            return null
        },
        referenceItem() {
            const firstValue = Object.values(this.checklist.reference)[0]
            const auditItem = this.referenceAuditParentItem
            const item = {
                ...firstValue,
                ...(auditItem || {}),
                is_visible: true,
                isAuditItem: true
            }

            // checklist.reference is not processed by the audit store, so photo values
            // can still be a string. Using value[0] on a URL string yields the first
            // character and the thumbnail/popup show a broken image.
            if ([FORMAT.PHOTO, FORMAT.SIGNATURE].includes(Number(item.type)) && item.value && typeof item.value === 'string') {
                item.value = splitCommasBase64(item.value)
            }

            return item
        },
        referenceAuditParentItem() {
            if (!this.referenceAudit || !this.checklist || !this.checklist.reference) {
                return null
            }

            const reference = Object.values(this.checklist.reference)[0]
            if (!reference) {
                return null
            }

            const sections = this.referenceAudit.sections || {}

            for (const sectionId in sections) {
                const section = sections[sectionId]
                if (section.items && section.items[reference.item_id]) {
                    return section.items[reference.item_id]
                }
            }

            return null
        },
        referenceAudit() {
            const audit = this.$store.getters['audit/getById'](Object.values(this.checklist.reference)[0].checklist_id)
            return audit
        },
        referenceAuditId() {
            return Object.values(this.checklist.reference)[0].checklist_id
        },
        referenceSectionName() {
            if (!this.referenceAudit || !this.referenceItem) {
                return this.checklist.audit_name || ''
            }

            const sections = this.referenceAudit.sections || {}

            for (const sectionId in sections) {
                const section = sections[sectionId]
                if (section.items && section.items[this.referenceItem.item_id]) {
                    return section.name || this.checklist.audit_name || ''
                }
            }

            return this.checklist.audit_name || ''
        },
        referenceSubitems() {
            if (!this.referenceAudit || !this.referenceItem) {
                return []
            }

            const sections = this.referenceAudit.sections || {}

            for (const sectionId in sections) {
                const section = sections[sectionId]
                if (section.items && section.items[this.referenceItem.item_id]) {
                    return getVisibleDescendants(section.items, this.referenceItem.item_id).map((item) => ({
                        ...item,
                        isAuditItem: true
                    }))
                }
            }

            return []
        },
        referenceDirectSubitemsCount() {
            if (!this.referenceItem || !this.referenceSubitems.length) {
                return 0
            }

            return this.referenceSubitems.filter((item) => item.parent_id === this.referenceItem.item_id).length
        },
        visibleItems() {
            // Filter and return only visible items, sorted by order
            if (!this.checklist || !this.checklist.items) {
                return []
            }

            return Object.values(this.checklist.items)
                .filter((item) => item.is_visible)
                .sort((a, b) => {
                    const orderA = parseFloat(a.order) || 0
                    const orderB = parseFloat(b.order) || 0
                    return orderA - orderB
                })
        },
        isActionPlanCompleted() {
            // Check if the action plan status is completed (type: 3)
            if (!this.checklist) {
                return false
            }

            const statusId = this.checklist.status
            if (!statusId) {
                return false
            }

            const actionPlanStatuses = this.$store.getters['actionplan/getAccountActionPlansStatuses']
            if (!actionPlanStatuses || actionPlanStatuses.length === 0) {
                return false
            }

            // Find the current status
            const currentStatus = actionPlanStatuses.find((status) => status.id === statusId)
            if (!currentStatus) {
                return false
            }

            // Check if the status type is 3 (Finalizado/Completed)
            // Use non-strict equality to handle API responses that may return type as string or number
            const isCompleted = currentStatus.type == 3

            return isCompleted
        },
        isDeadlineEnded() {
            if (!this.checklist || !this.checklist.end_date) {
                return false
            }
            const deadlineDate = new Date(this.checklist.end_date * 1000)
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            deadlineDate.setHours(0, 0, 0, 0)
            return deadlineDate < today
        },
        loggedUser() {
            return this.$store.getters['loginUser/getLocalEmployee']
        },
        user() {
            if (this.loggedUser) {
                return this.$store.getters['employee/getEmployee'](this.loggedUser)
            }
            return false
        },
        roles() {
            return this.$store.getters['employee/getAllRoles']
        },
        userRole() {
            if (this.user && this.user.rol) {
                return this.$store.getters['employee/getRol'](this.user.rol)
            }
            return null
        },
        hasNRolesConfig() {
            return this.nRoles && this.nRoles.length > 0
        },
        userRolePermissions() {
            if (!this.hasNRolesConfig || !this.user || !this.user.rol) {
                return null
            }
            const roleConfig = this.nRoles.find((role) => role.roleId === this.user.rol)

            return roleConfig ? roleConfig.permissions : null
        },
        canChangeStatus() {
            // si no hay Permisos de Gestión configurados, permitir cambiar el estado
            if (!this.hasNRolesConfig) {
                return true
            } else {
                // si el plan de acción está completado, permitir cambiar el estado solo si el usuario tiene permisos de Gestión,
                // si no está completado, permitir cambiar el estado si el usuario tiene permisos de Edición
                return this.isActionPlanCompleted ? this.userRolePermissions.includes('x') : this.userRolePermissions.includes('w')
            }
        },
        allMandatoryItemsCompleted() {
            // Verificar si todos los items y subitems obligatorios están completados
            if (!this.checklist || !this.checklist.items) {
                return false
            }

            const itemsExcluded = this.checklist.def && this.checklist.def.item && this.checklist.def.item.itemsExcluded ? this.checklist.def.item.itemsExcluded : []
            const allItems = Object.values(this.checklist.items || {})

            // Filtrar items obligatorios y visibles
            const mandatoryItems = allItems.filter((item) => {
                const isMandatory = item.mandatory === true || item.mandatory === 1 || item.mandatory === '1' || item.mandatory === 'true'
                const isVisible = item.is_visible !== false && item.is_visible !== 0 && item.is_visible !== '0'
                return isMandatory && isVisible && !itemsExcluded.includes(item.type) && item.type !== 8
            })

            // Si no hay items obligatorios, no mostrar popup
            if (mandatoryItems.length === 0) {
                return false
            }

            // Verificar que todos los items obligatorios estén completados
            const completedItems = mandatoryItems.filter((item) => {
                const hasCompleteDate = item.complete_date !== null && item.complete_date !== undefined && item.complete_date !== false && (typeof item.complete_date === 'number' || typeof item.complete_date === 'object' || (typeof item.complete_date === 'string' && item.complete_date.length > 0))

                let hasValidValue = true
                if (item.value === null || item.value === undefined || item.value === '' || item.value === 'null' || item.value === '""' || item.value === '\\"\\"' || (typeof item.value === 'string' && item.value.trim() === '')) {
                    hasValidValue = false
                }

                return hasCompleteDate && hasValidValue
            })

            return mandatoryItems.length === completedItems.length
        }
    },
    watch: {
        allMandatoryItemsCompleted(newVal, oldVal) {
            // Mostrar popup solo cuando cambia de false a true,
            // el estado actual no es tipo 3 (Finalizado),
            // y el usuario tiene permisos de escritura
            if (newVal && !oldVal && !this.hasShownCompletionPopup && !this.isActionPlanCompleted && this.checkPermissions()) {
                this.checkAllMandatory()
            }

            // Resetear el flag cuando el usuario borra respuestas
            // para permitir mostrar el popup de nuevo si vuelve a completar todo
            if (!newVal && oldVal) {
                this.hasShownCompletionPopup = false
            }
        }
    },
    methods: {
        checkAllMandatory() {
            // Verificar si el estado actual ya es tipo 3 (Finalizado)
            if (this.isActionPlanCompleted) {
                return
            }

            // Verificar que el usuario tiene permisos de escritura antes de mostrar el popup
            if (!this.checkPermissions()) {
                return
            }

            const self = this
            const userName = this.user && this.user.name ? this.user.name : ''

            // Marcar que ya se mostró el popup para no mostrarlo de nuevo
            this.hasShownCompletionPopup = true

            self.$popup.confirm({
                message: self.$t('action_plans.popup.mark_as_completed', { name: userName }),
                textSave: self.$t('action_plans.popup.mark_as_finished'),
                textCancel: self.$t('action_plans.popup.keep_open'),
                callSave: () => {
                    // Verificar que el checklist sigue existiendo antes de proceder
                    if (!self.checklist) {
                        return
                    }

                    // Obtener el estado con type 3 (Finalizado)
                    const finishedStatus = self.actionPlanStatuses.find((status) => {
                        const statusType = parseInt(status.type)
                        return statusType === 3
                    })

                    if (!finishedStatus) {
                        // Mostrar error si no se encuentra el estado Finalizado
                        logError('Error: No se encontró el estado Finalizado (type 3) en actionPlanStatuses')
                        self.$popup.alert({
                            message: self.$t('popup.alert.title'),
                            textSave: self.$t('popup.alert.text_save')
                        })
                        return
                    }

                    self.$overlay.loading()

                    self.$store
                        .dispatch('actionplan/update', {
                            id: self.checklist.id,
                            status: finishedStatus.id
                        })
                        .then(() => {
                            self.$overlay.hide()
                        })
                        .catch((error) => {
                            logError('Error updating status to finished:', error)
                            self.$overlay.hide()
                        })
                },
                callCancel: () => {
                    // Al hacer click en "Mantener abierto", solo cerrar el popup
                    self.$popup.close()
                }
            })
        },
        handleSelectorClick() {
            // Si la fecha límite ha pasado, no abrir el selector
            if (this.isDeadlineEnded) {
                const userInfo = this.$store.getters['loginUser/getUserInformation']
                const userName = userInfo && userInfo.name ? userInfo.name : ''

                this.$popup.alert({
                    message: this.$t('action_plans.deadline_ended_popup', { user_name: userName }),
                    textSave: this.$t('popup.alert.text_save')
                })
                return
            }

            // Verificar permisos: solo usuarios con permisos de escritura pueden cambiar el estado
            if (!this.checkPermissions()) {
                const userName = this.user && this.user.name ? this.user.name : ''
                this.$popup.alert({
                    message: this.$t('answer.timing.not_has_role_permission', {
                        name: userName,
                        roles: this.rolesWithWritePermissions()
                    })
                })
                return
            }

            if (!this.canChangeStatus) {
                this.isSelectorOpen = false
                return
            }

            this.isSelectorOpen = !this.isSelectorOpen
        },
        handleClickOutside(event) {
            if (this.$refs.stateSelector && !this.$refs.stateSelector.contains(event.target)) {
                this.isSelectorOpen = false
            }
        },
        formatDeadlineDate(timestamp) {
            // Format unix timestamp to DD/MM/YYYY
            if (!timestamp) return ''
            const date = new Date(timestamp * 1000)
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return `${day}/${month}/${year}`
        },
        changeStatus(statusId) {
            if (!this.checklist || !statusId) {
                return
            }

            // Si la fecha límite ha pasado, no permitir el cambio (lógica existente)
            if (this.isDeadlineEnded) {
                return
            }

            // Verificar permisos: solo usuarios con permisos de escritura pueden cambiar el estado
            if (!this.checkPermissions()) {
                const userName = this.user && this.user.name ? this.user.name : ''
                this.$popup.alert({
                    message: this.$t('answer.timing.not_has_role_permission', {
                        name: userName,
                        roles: this.rolesWithWritePermissions()
                    })
                })
                this.isSelectorOpen = false
                return
            }

            // Obtener el estado seleccionado
            const selectedStatus = this.actionPlanStatuses.find((status) => status.id === statusId)

            // Validar si el estado es tipo 3 (Finalizado)
            // Comparar tanto con número como con string para mayor robustez
            const isCompletedStatus = selectedStatus && (selectedStatus.type === 3 || selectedStatus.type === '3' || parseInt(selectedStatus.type) === 3)

            if (isCompletedStatus) {
                // Verificar que todos los items y subitems obligatorios estén completados
                if (!this.checklist || !this.checklist.items) {
                    // Si no hay checklist o items, no permitir el cambio
                    this.$popup.alert({
                        message: this.$t('action_plans.mandatory_msg')
                    })
                    this.isSelectorOpen = false
                    return
                }

                const itemsExcluded = this.checklist.def && this.checklist.def.item && this.checklist.def.item.itemsExcluded ? this.checklist.def.item.itemsExcluded : []
                const allItems = Object.values(this.checklist.items || {})
                let mandatory = true
                let incompleteItem = null

                // Validar items principales y subitems
                // Usar la misma lógica que en Issues (verificar value y complete_date)
                for (let i = 0; i < allItems.length; i++) {
                    const item = allItems[i]

                    // Verificar si el item es obligatorio (puede ser true, 1, o "1")
                    const isMandatory = item.mandatory === true || item.mandatory === 1 || item.mandatory === '1' || item.mandatory === 'true'

                    // Verificar si el item es visible (is_visible puede ser undefined, en ese caso asumimos que es true)
                    const isVisible = item.is_visible !== false && item.is_visible !== 0 && item.is_visible !== '0'

                    if (isMandatory && isVisible && !itemsExcluded.includes(item.type) && item.type !== 8) {
                        // Un item obligatorio está completado SOLO si tiene complete_date válido
                        // complete_date debe existir, no ser false, null o undefined
                        // Puede ser un timestamp (número) o un objeto Moment
                        const hasCompleteDate = item.complete_date !== null && item.complete_date !== undefined && item.complete_date !== false && (typeof item.complete_date === 'number' || typeof item.complete_date === 'object' || (typeof item.complete_date === 'string' && item.complete_date.length > 0))

                        // También verificar que el value sea válido (no null, no vacío, no "null", no "\"\"")
                        // Para campos de fecha y otros tipos, el value debe ser válido
                        let hasValidValue = true
                        if (item.value === null || item.value === undefined || item.value === '' || item.value === 'null' || item.value === '""' || item.value === '\\"\\"' || (typeof item.value === 'string' && item.value.trim() === '')) {
                            hasValidValue = false
                        }

                        // Un item obligatorio está incompleto si no tiene complete_date O no tiene value válido
                        if (!hasCompleteDate || !hasValidValue) {
                            mandatory = false
                            incompleteItem = item
                            break
                        }
                    }
                }

                // Si hay items obligatorios incompletos, hacer scroll y mostrar popup
                if (!mandatory) {
                    // Hacer scroll automático al primer item obligatorio no verificado
                    if (incompleteItem && incompleteItem.item_id) {
                        const element = document.getElementById(incompleteItem.item_id)
                        if (element) {
                            // Usar setTimeout para asegurar que el DOM esté actualizado
                            setTimeout(() => {
                                // Obtener el contenedor con scroll
                                const scrollContainer = this.$refs.container || document.getElementById('detail-content')

                                if (scrollContainer && element) {
                                    // Calcular la posición relativa del elemento dentro del contenedor
                                    const containerRect = scrollContainer.getBoundingClientRect()
                                    const elementRect = element.getBoundingClientRect()
                                    const scrollTop = scrollContainer.scrollTop
                                    const elementTop = elementRect.top - containerRect.top + scrollTop

                                    // Hacer scroll al inicio del elemento con un pequeño offset
                                    scrollContainer.scrollTo({
                                        top: elementTop - 100, // Offset de 100px desde arriba
                                        behavior: 'smooth'
                                    })
                                } else if (element) {
                                    // Fallback: usar scrollIntoView si no hay contenedor específico
                                    element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
                                }
                            }, 100)
                        }
                    }

                    this.$popup.alert({
                        message: this.$t('action_plans.mandatory_msg')
                    })
                    this.isSelectorOpen = false
                    return
                }
            }

            this.$overlay.loading()

            this.$store
                .dispatch('actionplan/update', {
                    id: this.checklist.id,
                    status: statusId
                })
                .then(() => {
                    this.$overlay.hide()
                    this.isSelectorOpen = false
                })
                .catch((error) => {
                    logError('Error updating status:', error)
                    this.$overlay.hide()
                })
        },
        toggleSubitemsSection(itemId) {
            if (this.closedSubitemsSections.includes(itemId)) {
                this.closedSubitemsSections = this.closedSubitemsSections.filter((id) => id !== itemId)
            } else {
                this.closedSubitemsSections.push(itemId)
            }
        },
        goToAuditList() {
            // Redirigir siempre a la vista de listado de templates de auditorías
            this.$router.push({ name: 'Audit' })
        },
        goBack() {
            const auditTemplateId = this.$route.params.auditTemplateId ? this.$route.params.auditTemplateId : null

            // Early validation
            if (!auditTemplateId) {
                this.$router.push({ name: 'Audit' })
                return
            }

            // Common fallback route
            const fallbackRoute = { name: 'AuditDetail', params: { id: auditTemplateId } }

            // to return to the audit checklist page
            const auditChecklistId = this.$route.params.auditChecklistId ? this.$route.params.auditChecklistId : null

            if (auditChecklistId) {
                this._navigateToAuditChecklist({
                    auditTemplateId: auditTemplateId,
                    auditChecklistId: auditChecklistId,
                    fallbackRoute: fallbackRoute
                })
                return
            }

            // If no previous route, use fallback
            if (!this.previousRoute || !this.previousRoute.path) {
                this.$router.push({
                    name: 'AuditDetail',
                    params: { id: auditTemplateId },
                    query: { tab: 'action_plan' }
                })
                return
            }

            // to return to the Audit/Action Plan tables page
            if (this.previousRoute.name === 'AuditDetail') {
                this._navigateToActionPlanTable({
                    auditTemplateId: auditTemplateId,
                    fallbackRoute: fallbackRoute
                })
                return
            }

            // For any other previous route, use fallback
            this.$router.push(fallbackRoute)
        },
        _navigateToAuditChecklist(params) {
            this.$overlay.loading()

            const auditTemplateId = params.auditTemplateId
            const auditChecklistId = params.auditChecklistId
            const fallbackRoute = params.fallbackRoute

            if (!auditTemplateId || !auditChecklistId) {
                this.$router.push(fallbackRoute)
                this.$overlay.hide()
                return
            }

            const queryParams = {
                fromActionPlanChecklist: true,
                nonConformityId: this.$route.query.nonConformityId ? this.$route.query.nonConformityId : null
            }

            this.$router
                .push({
                    path: `/audit/${auditTemplateId}/${auditChecklistId}`,
                    query: queryParams
                })
                .catch((error) => {
                    logError('Error navigating to audit checklist:', error)
                    this.$overlay.hide()
                })

            // The overlay will remain active until the destination view closes it when finished loading
        },
        _navigateToActionPlanTable(params) {
            this.$overlay.loading()

            const auditTemplateId = params.auditTemplateId
            const fallbackRoute = params.fallbackRoute

            if (!auditTemplateId) {
                this.$router.push(fallbackRoute)
                this.$overlay.hide()
                return
            }

            const previousTablePageNumber = this.$route && this.$route.params && this.$route.params.pageNumber ? this.$route.params.pageNumber : 1

            this.$store.commit('actionplan/setPageNumber', previousTablePageNumber)

            this.$router
                .push({
                    name: 'AuditDetail',
                    params: {
                        id: auditTemplateId,
                        shouldShowActionPlanTable: true,
                        pageNumber: previousTablePageNumber
                    }
                })
                .catch((error) => {
                    logError('Error navigating to action plan table:', error)
                    this.$overlay.hide()
                })

            // The overlay will remain active until the destination view closes it when finished loading
        },
        load() {
            const checklistId = this.$route.params.id
            this.closedSubitemsSections = []

            if (!checklistId) {
                return
            }

            this.$overlay.loading()

            this.$store
                .dispatch('actionplan/loadById', { id: checklistId })
                .then(() => {
                    const reference = this.checklist && this.checklist.reference
                    const firstReference = reference && Object.values(reference)[0]
                    const referenceAuditId = firstReference && firstReference.checklist_id

                    if (referenceAuditId && !this.referenceAudit) {
                        return this.$store.dispatch('audit/loadById', { id: referenceAuditId })
                    }
                })
                .finally(() => {
                    this.$overlay.hide()
                })
        },
        checkPermissions() {
            // Validar que el usuario existe
            if (!this.user || !this.user.rol) {
                return false
            }

            const checklist = this.checklist
            const userRole = this.$store.getters['employee/getRol'](this.user.rol)

            if (!userRole) {
                return false
            }

            // Si el usuario tiene nivel 8 o superior, tiene todos los permisos
            if (userRole.level >= 8) {
                return true
            }

            // Si el objeto config no existe o no tiene nRoles/roles, permitir edición (comportamiento por defecto)
            if (!checklist || !checklist.config || (!checklist.config.nRoles && !checklist.config.roles)) {
                return true
            }

            // Si existe config.nRoles, verificar permisos según roles
            if (checklist.config.nRoles && checklist.config.nRoles.length > 0) {
                return checklist.config.nRoles.some((role) => {
                    return role.roleId == userRole.id && role.permissions && role.permissions.includes('w')
                })
            }

            // Si existe config.roles (formato antiguo), mantener compatibilidad
            if (checklist.config.roles && checklist.config.roles.length > 0) {
                return checklist.config.roles.includes(this.user.rol)
            }

            // Por defecto, si no hay restricciones, permitir edición
            return true
        },
        checkReadPermissions() {
            // Validar que el usuario existe
            if (!this.user || !this.user.rol) {
                return false
            }

            const checklist = this.checklist
            const userRole = this.$store.getters['employee/getRol'](this.user.rol)

            if (!userRole) {
                return false
            }

            // Si el usuario tiene nivel 8 o superior, tiene todos los permisos
            if (userRole.level >= 8) {
                return true
            }

            // Si el objeto config no existe o no tiene nRoles/roles, permitir visualización (comportamiento por defecto)
            if (!checklist || !checklist.config || (!checklist.config.nRoles && !checklist.config.roles)) {
                return true
            }

            // Si existe config.nRoles, verificar permisos de lectura según roles
            if (checklist.config.nRoles && checklist.config.nRoles.length > 0) {
                return checklist.config.nRoles.some((role) => {
                    return role.roleId == userRole.id && role.permissions && role.permissions.includes('r')
                })
            }

            // Si existe config.roles (formato antiguo), mantener compatibilidad - permitir visualización
            if (checklist.config.roles && checklist.config.roles.length > 0) {
                return checklist.config.roles.includes(this.user.rol)
            }

            // Por defecto, si no hay restricciones, permitir visualización
            return true
        },
        detailPermissions() {
            if (this.checklist) {
                return this.checkPermissions()
            }
            return false
        },
        rolesWithWritePermissions() {
            const checklist = this.checklist
            const roles = []

            // Manejar formato nuevo: config.nRoles
            if (checklist && checklist.config && checklist.config.nRoles && checklist.config.nRoles.length > 0) {
                const idOfRoles = checklist.config.nRoles.filter((role) => role.permissions && role.permissions.includes('w')).map((role) => role.roleId)

                roles.push(
                    ...Object.values(this.roles)
                        .filter((role) => idOfRoles.includes(role.id))
                        .map((role) => role.name)
                )
            }
            // Manejar formato legacy: config.roles
            else if (checklist && checklist.config && checklist.config.roles && checklist.config.roles.length > 0) {
                roles.push(
                    ...Object.values(this.roles)
                        .filter((role) => checklist.config.roles.includes(role.id))
                        .map((role) => role.name)
                )
            }

            if (roles.length === 0) {
                return ''
            }

            return roles
                .join(', ')
                .toString()
                .replace(/,(?=[^,]*$)/, ' ' + this.$t('answer.or'))
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },
    created() {
        this.$bar.reset()
        this.$bar.hide()

        this.load()
    },
    beforeDestroy() {
        this.$bar.show()
        document.removeEventListener('click', this.handleClickOutside)
    },
    beforeRouteEnter(to, from, next) {
        // Guardar la ruta anterior antes de entrar al componente
        next((vm) => {
            vm.previousRoute = from
        })
    }
}
</script>

<style lang="scss" scoped>
#detail-content.action-plan {
    background-color: $default-bg-color;
    height: 100%;
    width: 100%;
    overflow-y: auto;
    padding-inline: 8px;

    .top-container {
        padding: 8px 0px;
        padding-bottom: 0;
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: inherit;

        ::v-deep #info-container {
            width: 100%;
        }

        ::v-deep .resume .score-box {
            margin-left: auto;
        }

        .action-plan-top-detail {
            .detail-progress-bar {
                padding: 16px 0px;
            }
        }
    }

    .action-plan-container {
        padding-bottom: 24px;

        .audit-name-display {
            align-self: stretch;
            color: #222;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
        }

        .non-conformity-container {
            display: flex;
            align-items: center;
            gap: 8px;
            padding-block: 12px;
            border-radius: 4px;

            .non-conformity-title {
                @include font-size(m);
                font-family: $text-bold;
                margin: 0;
            }

            .i-andy-exclamation {
                background-color: #757575;
                color: #fff;
                border-radius: 50%;
                font-size: 16px;
                padding: 3px;
            }
        }

        .action-plan-title-container {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 16px 0px 24px 0px;
            border-radius: 4px;

            .action-plan-title {
                @include font-size(m);
                font-family: $text-bold;
                color: #053186;
                margin: 0;
            }

            .i-andy-exclamation {
                background-color: #053186;
                color: #fff;
                border-radius: 50%;
                font-size: 16px;
                padding: 3px;
            }
        }

        .deadline-banner {
            &.ended {
                background: #fffacd;

                .i-andy-exclamation {
                    color: #b99000;
                }

                .deadline-message {
                    color: #222 !important;
                }
            }

            display: flex;
            padding: 10px;
            margin-top: 4px;
            margin-bottom: 12px;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            border-radius: 3px;
            background: #cfeefd;
            font-size: 14px;

            .i-andy-calendar {
                color: #2b40d8;
            }

            .deadline-message {
                color: #053186 !important;
            }
        }

        .action-plan-items-container {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 16px;
        }
    }

    .selector-container {
        margin-top: 20px;
        display: flex;
        justify-content: end;
        padding-right: 16px;

        .state-container {
            position: relative;
            height: 40px;
            border-radius: 4px 0 0 4px;
            width: 238px;

            .current-state {
                @include display-flex();
                @include align-items(center);
                @include border-radius(4px);
                @include background($image: img('down_caret_dark.svg'), $position: right 6px center, $size: 6px);
                border: 0.5px solid;
                padding: 6px;
                cursor: pointer;
                max-width: 238px;
                border: 1px solid $color-neutral-300;
                height: 40px;
                background-color: $color-white;

                .ball {
                    width: 10px;
                    height: 10px;
                    min-width: 10px;
                    min-height: 10px;
                    border-radius: 50%;
                    margin-right: 8px;
                }

                .name {
                    @include text-ellipsis();
                    color: $inactive-s80;
                    font-family: $text-bold;
                    padding-right: 10px;
                }

                &.isOpen {
                    @include background($image: img('up_caret_dark.svg'), $position: right 6px center, $size: 6px);
                }

                &.disabled {
                    cursor: not-allowed;
                }

                &.placeholder {
                    .name {
                        color: $color-neutral-600;
                        font-family: $text;
                    }
                }
            }

            .state-selector {
                @include border-radius(3px);
                top: 5px;
                position: relative;
                background-color: #fff;
                width: 100%;
                max-width: 238px;
                box-shadow: 0px 0px 10px 0px rgba(117, 115, 119, 0.3);
                z-index: 999;
                padding-bottom: 6px;

                .state {
                    width: 100%;
                    @include display-flex();
                    @include align-items(center);
                    @include border-radius(2px);
                    padding: 6px;
                    margin-right: 8px;
                    cursor: pointer;

                    .ball {
                        width: 10px;
                        height: 10px;
                        min-width: 10px;
                        min-height: 10px;
                        border-radius: 50%;
                        margin-right: 8px;
                        margin-left: 2px;
                    }

                    .name {
                        @include text-ellipsis();
                        color: $color-black;
                        max-width: 87%;
                        font-family: $text-bold;
                    }

                    &:hover {
                        @include bgHover(#fff);
                    }

                    &.selected {
                        background-color: #f4f4f4;

                        &:hover {
                            background: #f4f4f4;
                        }
                    }
                }
            }
        }
    }

    &.portrait {
        .action-plan-container {
            .non-conformity-container,
            .action-plan-title-container,
            .deadline-banner,
            .action-plan-items-container {
                width: 100%;
            }

            .reference-audit-section {
                margin-bottom: 0 !important;
            }

            .action-plan-title-container {
                padding: 8px 0;
            }

            .deadline-banner {
                margin-top: 0;
                padding: 8px;

                .deadline-message {
                    line-height: 18px;
                }
            }
        }
    }

    &.landscape,
    &.desktop {
        .action-plan-items-container {
            ::v-deep .questionCopy {
                padding-inline: 0;
            }
        }
    }

    &.desktop {
        padding-inline: 16px;
    }

    &.deadline-ended {
        ::v-deep .deadline-disabled,
        ::v-deep .question {
            opacity: 0.6;
            cursor: not-allowed;
            pointer-events: auto;

            * {
                pointer-events: none;
                cursor: not-allowed;
            }
        }

        .selector-container {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}

// Empty state
.action-plan.empty {
    display: flex;
    align-items: center;
    justify-content: center;
}

.section-content {
    .audit {
        float: none !important;
    }

    @include display-flex();
    @include flex-direction(row);
    @include flex-wrap(wrap);
    @include border-radius($default-border-radius);

    @media (min-width: 901px) {
        gap: 20px;
    }

    .action-plan-section {
        width: 100%;
        // padding-bottom: 24px;

        .title-and-button {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 4px;
        }

        .action-plan-title {
            font-size: 18px;
        }

        .show-hide {
            display: flex;
            gap: 4px;
            align-items: center;
            background-color: none;

            .label {
                color: #374673;
                font-size: 18px;
                font-weight: bold;
            }
        }

        .show-hide:hover {
            background-color: transparent;
        }

        .action-plan-items-container {
            padding-left: 16px;
        }
    }

    .question-score {
        @include display-inline-flex();
        // @include justify-content();
        @include align-items();
        @include font-size(m);
        margin: 20px auto;
        font-family: $text;
        color: $main;
        width: 80px;
        height: 50px;
    }

    .subitems {
        padding: 0px !important;
        width: calc(100% - 48px);
        margin-left: 48px;
        margin-top: 8px;

        @media (max-width: 767px) {
            width: calc(100% - 16px);
            margin-left: 16px;
        }

        .section-title {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            padding: 8px 0;
            background-color: transparent;
            border: none;

            .subitems-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                border-radius: 24%;
                background-color: var(--color-secondary-800, #092058);
                flex-shrink: 0;

                span {
                    color: #fff;
                    font-size: 18px;
                }
            }

            .subitems-text {
                color: var(--color-secondary-800, #092058);
                font-family: 'Avenir Next LT Pro', sans-serif;
                font-size: 16px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
            }
        }

        .section-content {
            @include display-flex();
            @include flex-direction(row);
            @include flex-wrap(wrap);
            @include border-radius($default-border-radius);

            @media (min-width: 901px) {
                gap: 20px;
                padding-top: 8px;
            }
        }
    }

    .section {
        padding: 24px 8px;
        width: 100%;
    }
}
</style>
<style lang="scss">
.reference-audit-section {
    float: none !important;
    margin-bottom: 20px !important;

    .value {
        cursor: no-drop !important;
        pointer-events: none !important;
    }

    .comment {
        display: none !important;
    }

    .question {
        padding: 0 !important;

        &.subsubitem {
            padding-left: 40px !important;
        }
    }
}

.action-plan {
    .action-plan-container {
        .deadline-banner {
            &.ended {
                b,
                strong {
                    color: #222 !important;
                    font-weight: 700 !important;
                    font-family: $text-bold;
                }
            }

            b,
            strong {
                color: #053186 !important;
                font-weight: 700 !important;
                font-family: $text-bold;
            }
        }
    }
}

#detail-content.action-plan {
    #content.audit.detail .question {
        margin: 10px 0 0 0 !important;
    }

    .section-content {
        gap: 10px !important;
    }
}
</style>
